// Storage adapter for authentication credentials.
// Provides in-memory caching and storage for JWT access tokens and
// HttpOnly cookie refresh sessions, ensuring refresh tokens are never persisted in localStorage.

const V1_KEY = 'wgt_auth_v1';
const LEGACY_STUDIO_V1_KEY = 'wgt_studio_auth_v1';
const DEVICE_KEY = 'wgt_device_id';
const HIJACK_KEY = 'wgt_studio_hijack';
const LEGACY_TOKEN_KEY = 'token';

let inMemoryTokens = null;
let inMemoryDeviceId = null;
let inMemoryHijack = false;

function safeGet(storage, key) {
  try {
    if (typeof window !== 'undefined' && window[storage]) {
      return window[storage].getItem(key);
    }
  } catch (e) {
    // Storage disabled or blocked (e.g. private browsing or cross-origin restrictions)
  }
  return null;
}

function safeSet(storage, key, value) {
  try {
    if (typeof window !== 'undefined' && window[storage]) {
      window[storage].setItem(key, value);
    }
  } catch (e) {
    // Storage disabled or quota exceeded
  }
}

function safeRemove(storage, key) {
  try {
    if (typeof window !== 'undefined' && window[storage]) {
      window[storage].removeItem(key);
    }
  } catch (e) {
    // Storage disabled
  }
}

const AuthStorage = {
  // Shared JWT access token, cached in-memory and synchronized with the store.
  // Refresh tokens are NEVER stored in localStorage (they live solely in HttpOnly cookies).
  getTokens() {
    const raw = safeGet('localStorage', V1_KEY);
    if (raw) {
      try {
        const pair = JSON.parse(raw);
        if (pair && pair.access_token) {
          const legacyRefreshToken = pair.refresh_token;
          // Purge any legacy leaked refresh token from localStorage
          if (pair.refresh_token) {
            delete pair.refresh_token;
            safeSet('localStorage', V1_KEY, JSON.stringify(pair));
          }

          if (
            !inMemoryTokens ||
            inMemoryTokens.accessToken !== pair.access_token
          ) {
            inMemoryTokens = {
              accessToken: pair.access_token,
              refreshToken: legacyRefreshToken,
              expiresIn: pair.expires_in,
              obtainedAt: pair.obtained_at || Date.now(),
            };
          }
          return inMemoryTokens;
        }
      } catch (e) {
        return inMemoryTokens;
      }
    } else {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          inMemoryTokens = null;
        }
      } catch (e) {
        // storage disabled, keep inMemoryTokens
      }
    }
    return inMemoryTokens;
  },

  // Accepts the raw (snake_case) token response.
  // Refresh tokens are intentionally omitted from localStorage (kept only in HttpOnly cookies).
  setTokens(pair) {
    if (!pair || !pair.access_token) return;
    const obtainedAt = Date.now();
    inMemoryTokens = {
      accessToken: pair.access_token,
      refreshToken: pair.refresh_token,
      expiresIn: pair.expires_in,
      obtainedAt,
    };
    safeSet(
      'localStorage',
      V1_KEY,
      JSON.stringify({
        access_token: pair.access_token,
        expires_in: pair.expires_in,
        obtained_at: obtainedAt,
      })
    );
  },

  clearTokens() {
    inMemoryTokens = null;
    safeRemove('localStorage', V1_KEY);
    safeRemove('localStorage', LEGACY_STUDIO_V1_KEY);
  },

  clearLegacyStudioTokens() {
    safeRemove('localStorage', LEGACY_STUDIO_V1_KEY);
  },

  // Device ID for token binding
  getDeviceId() {
    if (inMemoryDeviceId) return inMemoryDeviceId;
    const id = safeGet('localStorage', DEVICE_KEY);
    if (id) {
      inMemoryDeviceId = id;
      return id;
    }
    return null;
  },

  setDeviceId(id) {
    inMemoryDeviceId = id;
    safeSet('localStorage', DEVICE_KEY, id);
  },

  // Admin Hijack Session
  isHijack() {
    if (inMemoryHijack) return true;
    const val = safeGet('sessionStorage', HIJACK_KEY);
    return val === '1';
  },

  setHijack(active) {
    inMemoryHijack = Boolean(active);
    if (active) {
      safeSet('sessionStorage', HIJACK_KEY, '1');
    } else {
      safeRemove('sessionStorage', HIJACK_KEY);
    }
  },

  clearHijack() {
    inMemoryHijack = false;
    safeRemove('sessionStorage', HIJACK_KEY);
  },

  // Legacy token cleanup
  clearLegacyToken() {
    safeRemove('localStorage', LEGACY_TOKEN_KEY);
  },

  // Reset internal memory state (useful for tests and logouts)
  resetMemoryState() {
    inMemoryTokens = null;
    inMemoryDeviceId = null;
    inMemoryHijack = false;
  },
};

export default AuthStorage;
